import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiBuilding, FiMapPin, FiUsers, FiEdit, FiTrash2, FiPlus, FiSave, FiX } = FiIcons;

function PrisonManagement() {
  const { prisons, orders, inventory } = useApp();
  const [editingPrison, setEditingPrison] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newPrison, setNewPrison] = useState({
    name: '',
    location: '',
    capacity: '',
    type: 'Category B',
    manager: ''
  });

  const handleEdit = (prison) => {
    setEditingPrison({ ...prison });
    setIsAddingNew(false);
  };

  const handleSave = () => {
    // In a real app, this would update the prison data
    console.log('Saving prison:', editingPrison);
    setEditingPrison(null);
  };

  const handleDelete = (prisonId) => {
    if (window.confirm('Are you sure you want to delete this prison? This action cannot be undone.')) {
      // In a real app, this would delete the prison
      console.log('Deleting prison:', prisonId);
    }
  };

  const handleAddNew = () => {
    if (newPrison.name && newPrison.location && newPrison.capacity && newPrison.manager) {
      // In a real app, this would add the new prison
      console.log('Adding new prison:', newPrison);
      setNewPrison({
        name: '',
        location: '',
        capacity: '',
        type: 'Category B',
        manager: ''
      });
      setIsAddingNew(false);
    }
  };

  const getPrisonStats = (prisonId) => {
    const prisonOrders = orders.filter(order => order.prisonId === prisonId);
    const prisonRevenue = prisonOrders.reduce((sum, order) => sum + order.total, 0);
    const prisonItems = inventory[prisonId]?.length || 0;
    
    return {
      orders: prisonOrders.length,
      revenue: prisonRevenue,
      items: prisonItems
    };
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-3xl font-bold text-government-900">
            Prison Management
          </h1>
          <button
            onClick={() => setIsAddingNew(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5" />
            <span>Add Prison</span>
          </button>
        </div>
        <p className="text-government-600">
          Manage all prisons in the AB Orders system
        </p>
      </motion.div>

      {/* Add New Prison Form */}
      {isAddingNew && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-6"
        >
          <h3 className="text-lg font-bold text-government-900 mb-4">Add New Prison</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              value={newPrison.name}
              onChange={(e) => setNewPrison({ ...newPrison, name: e.target.value })}
              className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Prison name"
            />
            <input
              type="text"
              value={newPrison.location}
              onChange={(e) => setNewPrison({ ...newPrison, location: e.target.value })}
              className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Location"
            />
            <input
              type="number"
              value={newPrison.capacity}
              onChange={(e) => setNewPrison({ ...newPrison, capacity: e.target.value })}
              className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Capacity"
            />
            <select
              value={newPrison.type}
              onChange={(e) => setNewPrison({ ...newPrison, type: e.target.value })}
              className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="Category A">Category A</option>
              <option value="Category B">Category B</option>
              <option value="Category C">Category C</option>
              <option value="Category D">Category D</option>
            </select>
            <input
              type="text"
              value={newPrison.manager}
              onChange={(e) => setNewPrison({ ...newPrison, manager: e.target.value })}
              className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500 md:col-span-2"
              placeholder="Manager name"
            />
          </div>
          <div className="flex space-x-2 mt-4">
            <button
              onClick={handleAddNew}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
            >
              <SafeIcon icon={FiSave} className="w-4 h-4" />
              <span>Add Prison</span>
            </button>
            <button
              onClick={() => setIsAddingNew(false)}
              className="flex items-center space-x-2 px-4 py-2 bg-government-600 hover:bg-government-700 text-white rounded-lg font-medium transition-colors"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
              <span>Cancel</span>
            </button>
          </div>
        </motion.div>
      )}

      {/* Prison Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {prisons.map((prison, index) => {
          const stats = getPrisonStats(prison.id);
          const isEditing = editingPrison && editingPrison.id === prison.id;
          
          return (
            <motion.div
              key={prison.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              {isEditing ? (
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-government-900 mb-4">Edit Prison</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={editingPrison.name}
                      onChange={(e) => setEditingPrison({ ...editingPrison, name: e.target.value })}
                      className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Prison name"
                    />
                    <input
                      type="text"
                      value={editingPrison.location}
                      onChange={(e) => setEditingPrison({ ...editingPrison, location: e.target.value })}
                      className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Location"
                    />
                    <input
                      type="number"
                      value={editingPrison.capacity}
                      onChange={(e) => setEditingPrison({ ...editingPrison, capacity: e.target.value })}
                      className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Capacity"
                    />
                    <select
                      value={editingPrison.type}
                      onChange={(e) => setEditingPrison({ ...editingPrison, type: e.target.value })}
                      className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="Category A">Category A</option>
                      <option value="Category B">Category B</option>
                      <option value="Category C">Category C</option>
                      <option value="Category D">Category D</option>
                    </select>
                    <input
                      type="text"
                      value={editingPrison.manager}
                      onChange={(e) => setEditingPrison({ ...editingPrison, manager: e.target.value })}
                      className="px-3 py-2 border border-government-300 rounded-lg focus:ring-2 focus:ring-primary-500 md:col-span-2"
                      placeholder="Manager name"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSave}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                    >
                      <SafeIcon icon={FiSave} className="w-4 h-4" />
                      <span>Save</span>
                    </button>
                    <button
                      onClick={() => setEditingPrison(null)}
                      className="flex items-center space-x-2 px-4 py-2 bg-government-600 hover:bg-government-700 text-white rounded-lg font-medium transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-4 h-4" />
                      <span>Cancel</span>
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="bg-government-100 p-3 rounded-lg">
                        <SafeIcon icon={FiBuilding} className="w-6 h-6 text-government-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-government-900">
                          {prison.name}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          prison.type === 'Category A' 
                            ? 'bg-red-100 text-red-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {prison.type}
                        </span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(prison)}
                        className="p-2 text-government-600 hover:text-primary-600 transition-colors"
                      >
                        <SafeIcon icon={FiEdit} className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(prison.id)}
                        className="p-2 text-government-600 hover:text-red-600 transition-colors"
                      >
                        <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center text-government-600">
                      <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-2" />
                      <span className="text-sm">{prison.location}</span>
                    </div>
                    <div className="flex items-center text-government-600">
                      <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2" />
                      <span className="text-sm">Capacity: {prison.capacity}</span>
                    </div>
                    <div className="text-sm text-government-600">
                      <span className="font-medium">Manager:</span> {prison.manager}
                    </div>
                  </div>

                  <div className="border-t border-government-200 pt-4">
                    <h4 className="font-medium text-government-900 mb-2">Performance</h4>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-lg font-bold text-primary-600">{stats.orders}</p>
                        <p className="text-xs text-government-600">Orders</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-green-600">£{stats.revenue.toFixed(0)}</p>
                        <p className="text-xs text-government-600">Revenue</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-blue-600">{stats.items}</p>
                        <p className="text-xs text-government-600">Menu Items</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

export default PrisonManagement;